import React from 'react';
import {useIntl} from '@sail/intl';
import {css, view} from '@sail/ui';
import type {UploadedFile} from './FileUploaderFileListItem';
import {FileUploaderFileListItem} from './FileUploaderFileListItem';
import {messages} from './messages';
import {styles} from './styles';
import {getTotalFileSize} from './utils';
import type {StripeFileFragmentData} from './stripeFileFragment';

// Sorts the list of uploaded files by the `created` timestamp
// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export function sortFilesByCreated(
  file1: UploadedFile,
  file2: UploadedFile,
): number {
  // if (!file1.created && !file2.created) return 0; // keep original order
  // if (!file1.created && file2.created) return 1; // place file1 after file2
  // if (file1.created && !file2.created) return -1; // place file1 before file2
  // return file1.created - file2.created;
  return (
    (file1.created || Number.MAX_SAFE_INTEGER) -
    (file2.created || Number.MAX_SAFE_INTEGER)
  );
}

const UploadedFileSize = ({
  maxSize,
  totalSize,
}: {
  maxSize: number;
  totalSize: number;
}) => {
  const intl = useIntl();

  return (
    <view.div css={{stack: 'x', alignY: 'center', gap: 'small'}}>
      <view.span uses={[styles.totalSizeStyles]}>
        {intl.formatMessage(messages.totalSize, {
          maxSize: intl.formatNumber(maxSize / 1e6, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 2,
          }),
          remainingSize: intl.formatNumber((maxSize - totalSize) / 1e6, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 2,
          }),
        })}
      </view.span>
      <view.div uses={[styles.progressBarContainerStyles]}>
        <view.div
          uses={[
            styles.progressBarStyles,
            css({
              width: `${(totalSize / maxSize) * 100}%`,
            }),
          ]}
        />
      </view.div>
    </view.div>
  );
};

type Props = {
  categories?: {category: string; message: string}[];
  categoryError?: string;
  disabled?: boolean;
  hasUniqueCategories?: boolean;
  maxSize?: number;
  onCategoryChange?: (
    fileId: string,
    newCategory?: string,
    previousCategory?: string,
  ) => void;
  onChange?: (files: UploadedFile[]) => void;
  onError?: (error: Error) => void;
  onRemove?: (fileId: string, category?: string) => void;
  onSuccess?: (fileId: string) => void;
  purpose: string;
  showFileSize?: boolean;
  showSizeUploaded?: boolean;
  showPreview?: boolean;
  uploadedFiles: UploadedFile[];
  consentedToShareWithPlatform?: boolean;
  disableDownload?: boolean;
};

export const FileUploaderFileList: React.FC<Props> = ({
  categories = [],
  categoryError,
  disabled,
  hasUniqueCategories,
  maxSize,
  onCategoryChange,
  onChange,
  onError,
  onRemove,
  onSuccess,
  purpose,
  showFileSize,
  showSizeUploaded,
  showPreview,
  uploadedFiles,
  consentedToShareWithPlatform,
  disableDownload,
}) => {
  /**
   *  Here, we need to use a ref to store a pointer to field.value.
   *  This is because react-query's options variable - https://github.com/TanStack/query/blob/beta/src/reactjs/useMutation.ts#L78
   *  is not included in the mutate useCallback dependency array, thus the mutate function returned does not change even if options change.
   *  As a result, react-query is holding onto a stale copy of `onFileSuccessfullyUploaded`, more specifically, the
   *  uploadedFiles variable used in the function.
   *  Also, the field object returned by useField is not stable, thus it does not mutate to have the correct
   *  value within react-query's MutationObserver despite updating itself.
   *
   *  One alternative was in useDisputeSubmitEvidenceUploadFile:
   *    const success = React.useRef(onFileSuccessfullyUploaded);
   *    React.useEffect(() => {success.current = onFileSuccessfullyUploaded;});
   *    onSuccess: success.current;
   * */
  const uploadedFilesRef = React.useRef<UploadedFile[]>(uploadedFiles);
  React.useEffect(() => {
    uploadedFilesRef.current = uploadedFiles;
  }, [uploadedFiles]);

  // This function sets the stripeFile on the state once we have obtained it from the API
  const onFileSuccessfullyUploaded = React.useCallback(
    (fileId: string, stripeFile: StripeFileFragmentData) => {
      if (!onChange) return;
      const newFiles = uploadedFilesRef.current
        .map((file) => {
          if (file.id !== fileId) {
            return file;
          }
          const succeededFile = {
            ...file,
            /**
             * All uploaded files are arranged in the order that they were uploaded.
             * However, for retrieved files, there is a possibility that we do not know their
             * created property. Therefore, we only set this when we have
             * successfully retrieved the file.
             */
            id: stripeFile.id,
            created: file.created || stripeFile.created,
            stripeFile,
          };
          if (onSuccess) {
            onSuccess(stripeFile.id);
          }
          return succeededFile;
        })
        .sort(sortFilesByCreated); // sort by created in ascending order
      uploadedFilesRef.current = newFiles;
      onChange(newFiles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadedFilesRef, onSuccess],
  );

  const totalSize = getTotalFileSize(uploadedFiles);

  const selectedCategories = React.useMemo(() => {
    if (!hasUniqueCategories) return undefined;
    return new Set(
      uploadedFiles
        .filter(({category}) => category !== undefined)
        .map(({category}) => category) as string[],
    );
  }, [uploadedFiles, hasUniqueCategories]);

  if (uploadedFiles.length === 0) return null;

  return (
    <>
      {showSizeUploaded && maxSize && (
        <UploadedFileSize maxSize={maxSize} totalSize={totalSize} />
      )}
      <view.div
        css={{
          backgroundColor: 'surface',
          borderColor: 'border',
          borderWidth: 'small',
          borderStyle: 'solid',
          borderRadius: 'small',
          padding: 'small',
        }}
      >
        {uploadedFiles.map((file, idx) => (
          <FileUploaderFileListItem
            key={file.id}
            categories={categories}
            categoryError={categoryError}
            disabled={disabled}
            onCategoryChange={onCategoryChange}
            onFileSuccessfullyUploaded={onFileSuccessfullyUploaded}
            onError={onError}
            onRemove={onRemove}
            purpose={purpose}
            selectedCategories={selectedCategories}
            showDivider={idx !== uploadedFiles.length - 1}
            showFileSize={showFileSize}
            showPreview={showPreview}
            uploadedFile={file}
            consentedToShareWithPlatform={consentedToShareWithPlatform}
            disableDownload={disableDownload}
          />
        ))}
      </view.div>
    </>
  );
};
